import {
  HTMLAttributes,
  ReactElement,
  SyntheticEvent,
  useState,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import {
  Autocomplete,
  AutocompleteValue,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  AutocompleteInputChangeReason,
  AutocompleteFreeSoloValueMapping,
  AutocompleteRenderInputParams,
  TextField,
  Grid,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import {
  useCustomersAutocomplete,
  CustomerPrediction,
} from '~hooks/useCustomersAutocomplete';
import { getCustomerIdentifier } from '~utils/customer';

import { CustomersAutocompleteProps } from './types';

const getOptionLabel = <FreeSolo extends boolean | undefined = undefined>(
  option: CustomerPrediction | AutocompleteFreeSoloValueMapping<FreeSolo>,
): string => {
  if (typeof option === 'string') return option;

  return getCustomerIdentifier(option);
};

const isOptionEqualToValue = (
  option: CustomerPrediction,
  value: CustomerPrediction,
): boolean => option.customerId === value.customerId;

function CustomersAutocomplete<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: CustomersAutocompleteProps<Multiple, DisableClearable, FreeSolo>,
): ReactElement {
  const {
    id = 'customers-autocomplete',
    loadingText = 'Cargando...',
    noOptionsText = 'Sin opciones',
    disabled,
    readOnly,
    label = 'Contacto',
    error = false,
    helperText,
    onChange: onChangeProps,
    onInputChange: onInputChangeProps,
    innerRef,
    multiple,
    initialValueRequest,
    disableClearable,
    freeSolo,
    ...rest
  } = props;

  const {
    value,
    setValue,
    inputValue,
    setInputValue,
    options,
    setOptions,
    loading,
  } = useCustomersAutocomplete({
    initialValue: initialValueRequest,
    multiple,
    disableClearable,
    freeSolo,
  });

  const [isSearched, setIsSearched] = useState(false);

  const onChange = useCallback(
    (
      event: SyntheticEvent,
      newValue: AutocompleteValue<
        CustomerPrediction,
        Multiple,
        DisableClearable,
        FreeSolo
      >,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<CustomerPrediction>,
    ) => {
      const newOptions = (
        newValue !== null ? (multiple ? newValue : [newValue]) : []
      ) as CustomerPrediction[];

      setOptions([...newOptions, ...options]);
      setValue(newValue);

      onChangeProps?.(event, newValue, reason, details);
    },
    [multiple, setOptions, options, setValue, onChangeProps],
  );

  const onInputChange = useCallback(
    (
      event: SyntheticEvent,
      newValue: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      setInputValue(newValue);

      onInputChangeProps?.(event, newValue, reason);
    },
    [setInputValue, onInputChangeProps],
  );

  const openMenu = useMemo(() => {
    if (!isSearched) return false;

    return inputValue.length >= 2;
  }, [inputValue, isSearched]);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams): JSX.Element => (
      <TextField
        {...params}
        label={label}
        error={error}
        helperText={helperText}
        fullWidth
      />
    ),
    [label, error, helperText],
  );

  const renderOption = useCallback(
    (
      optionProp: HTMLAttributes<HTMLLIElement>,
      option: CustomerPrediction,
    ): JSX.Element => {
      const description = getOptionLabel<FreeSolo>(option);

      const matches = match(description, inputValue, { insideWords: true });

      const parts = parse(description, matches);

      return (
        <li {...optionProp}>
          <Grid container alignItems="center">
            <Grid item>
              <Box
                component={PersonIcon}
                sx={{ color: 'text.secondary', mr: 2 }}
              />
            </Grid>

            <Grid item xs>
              {parts.map((part, index) => (
                <span
                  key={index}
                  style={{ fontWeight: part.highlight ? 700 : 400 }}
                >
                  {part.text}
                </span>
              ))}

              {option.customerType && (
                <Chip
                  label={option.customerType}
                  size="small"
                  sx={{ marginLeft: 0.5 }}
                />
              )}

              <Typography variant="body2" color="text.secondary">
                {option.address}
              </Typography>
            </Grid>
          </Grid>
        </li>
      );
    },
    [inputValue],
  );

  const resetAutocomplete = useCallback((): void => {
    setValue(
      (multiple ? [] : null) as AutocompleteValue<
        CustomerPrediction,
        Multiple,
        DisableClearable,
        FreeSolo
      >,
    );
    setInputValue('');
  }, [setValue, multiple, setInputValue]);

  useImperativeHandle(
    innerRef,
    () => ({
      resetAutocomplete,
    }),
    [resetAutocomplete],
  );

  return (
    <Autocomplete
      id={id}
      multiple={multiple}
      disabled={disabled || readOnly}
      getOptionLabel={getOptionLabel}
      options={options}
      autoComplete
      includeInputInList={false}
      filterSelectedOptions
      isOptionEqualToValue={isOptionEqualToValue}
      value={value}
      inputValue={inputValue}
      onChange={onChange}
      onInputChange={onInputChange}
      renderInput={renderInput}
      renderOption={renderOption}
      onClose={() => setIsSearched(false)}
      onOpen={() => setIsSearched(true)}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      loading={loading}
      open={openMenu}
      disableClearable={disableClearable}
      freeSolo={freeSolo}
      {...rest}
    />
  );
}

export default CustomersAutocomplete;
